import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { useNotificationContext } from "@/contexts/notifications/NotificationProvider";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "lucide-react";
import { QuickActions } from "@/components/pollster-dashboard/QuickActions";
import { DashboardStats } from "@/components/pollster-dashboard/DashboardStats";
import { DocumentsList } from "@/components/pollster-dashboard/DocumentsList";
import { NotificationsList } from "@/components/pollster-dashboard/NotificationsList";
import { usePollsterData } from "@/hooks/pollster-dashboard/usePollsterData";
import { UserHistory } from "@/components/profile/UserHistory";

const PollsterDashboard = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { notifications, unreadCount } = useNotificationContext();
  const { pollStats, loading, documents } = usePollsterData(user?.id);

  // Redirect if not logged in or not a verified pollster
  useEffect(() => {
    if (!profile) return;
    
    if (profile.role !== 'pollster' || !profile.verified) {
      toast.error('You must be a verified pollster to access this dashboard');
      navigate('/profile');
    }
  }, [profile, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-purple"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-[97px] pb-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Pollster Dashboard</h1>
            <p className="text-muted-foreground">
              Welcome back, {profile?.name || user?.email}
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-6 mb-8">
            <QuickActions />

            <div className="grid grid-cols-1 md:grid-cols-2 md:w-2/3 gap-6">
              <DashboardStats pollStats={pollStats} />
              <DocumentsList documents={documents} />
              <NotificationsList 
                notifications={notifications} 
                unreadCount={unreadCount} 
              />
            </div>
          </div>

          {/* Voting History */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                <Calendar className="mr-2 h-5 w-5 text-nation-blue" />
                My Voting History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UserHistory userId={user?.id} />
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PollsterDashboard;
